import { Alert, Box, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormEvent, PropsWithChildren, useCallback, useState } from 'react';
import { useBoolean, useSessionStorage } from 'usehooks-ts';

import { getCurrentUser, getOtp } from '@/api/auth';
import { createNewOperationRequest } from '@/api/operation';
import { updateBiometricConsent } from '@/api/user';

import { CountDownButton } from '@/components/Form/CountDownButton';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { OTPInput } from '@/components/Form/OTPInput';
import { Icon } from '@/components/Icon';
import { Form } from '@/components/Layout';

import { SS_BIOMETRIC_CONSENT } from '@/constants/common';
import { getTenantConfigs } from '@/constants/tenant-configs';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { CountrySelectionForm } from '@/pages/onboarding/country-select/types/form';

import {
  notifyUserChanged,
  setLoggedInUser,
  signOut,
} from '@/redux/auth/actions';
import { selectConfigsState } from '@/redux/configs/selectors';
import { selectTerritories } from '@/redux/countries';

import { ICBResponseCode, OperationType, User } from '@/types/user';

import { getAPIErrorMessage } from '@/utils/api';
import { getDataTestId, openICBLinkByPlatform } from '@/utils/browser';
import { openRepublicSuperApp } from '@/utils/superApp';
import { isCaymanTenant } from '@/utils/tenant';
import { isRejectedUser, isRemediationCustomer } from '@/utils/user';
import { isAbove18 } from '@/utils/validation';

import { OTP_LENGTH } from '../constants';
import { useVerifyOTP } from '../hooks/useVerifyOTP';

type VerifyEmailOtp = {
  countryForm: CountrySelectionForm;
  onPendingProfileChange?: (pending: boolean) => void;
  type?: OperationType;
} & PropsWithChildren;

export function VerifyEmailOtp({
  countryForm,
  onPendingProfileChange,
  type,
  children,
}: VerifyEmailOtp) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { territoryByCountryCode } = useAppSelector(selectTerritories);
  const {
    value: isCreatingNewOperation,
    setTrue: setIsCreatingNewOperation,
    setFalse: setIsCreatingNewOperationFalse,
  } = useBoolean(false);
  const { links: configLinks, canAbandonApplication } =
    useAppSelector(selectConfigsState);
  const [biometricConsent, _, removeBiometricConsent] = useSessionStorage(
    SS_BIOMETRIC_CONSENT,
    '',
  );

  const { mutate: mutateBiometricConsent } = useMutation({
    mutationFn: updateBiometricConsent,
  });

  const territory = territoryByCountryCode[countryForm?.countryCode || ''];
  const tenant = territory?.tenant;
  const icbLinks =
    tenant && getTenantConfigs(tenant)?.links
      ? getTenantConfigs(tenant).links
      : configLinks;
  const [otp, setOtp] = useState('');

  const checkForCreateNewOperation = async (user: User) => {
    if (
      canAbandonApplication &&
      isRejectedUser(user) &&
      isAbove18(user.birthday)
    ) {
      setIsCreatingNewOperation();
      await createNewOperationRequest();
      return true;
    }
    return false;
  };

  const { mutate, isPending, error } = useVerifyOTP({
    onLoggedIn: async () => {
      const { user } = await getCurrentUser(false);
      if (
        user.icbResponseCode === ICBResponseCode.ExistingCustomerProfilePending
      ) {
        onPendingProfileChange?.(true);
        dispatch(signOut());
        return;
      }
      if (user) {
        const shouldCreateNewOperation = await checkForCreateNewOperation(user);
        let operationUser: User = user;
        if (shouldCreateNewOperation) {
          const { user: newUser } = await getCurrentUser(false);
          setIsCreatingNewOperationFalse();
          operationUser = newUser;
        }
        dispatch(setLoggedInUser(operationUser));
      }
      dispatch(notifyUserChanged({ resetInit: true }));

      if (user?.biometricConsent === undefined && biometricConsent !== '') {
        mutateBiometricConsent({
          biometricConsent: biometricConsent === 'true',
        });
        removeBiometricConsent();
      }
    },
  });

  const onVerify = (e: FormEvent) => {
    e.preventDefault();

    mutate({
      payload: {
        email: countryForm.email,
        passcode: otp,
        type,
      },
      tenant: territory?.tenant,
    });
  };

  const onResend = useCallback(() => {
    if (countryForm) {
      getOtp({ email: countryForm.email, tenant: territory?.tenant });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryForm]);

  const onOpenBankingApp = useCallback(() => {
    if (isRemediationCustomer({ type })) {
      openRepublicSuperApp();
      return;
    }

    if (icbLinks) {
      openICBLinkByPlatform(icbLinks);
    }
  }, [icbLinks, type]);

  if (!countryForm) return null;

  /** ExC-specific UI applies to Group tenants only, excluding Cayman */
  const showExcEmailOtpUi =
    type && type !== OperationType.New && !isCaymanTenant(tenant);

  return (
    <Form onSubmit={onVerify}>
      {children}
      <OTPInput
        length={OTP_LENGTH}
        value={otp}
        onChange={setOtp}
        disabled={isPending}
      />
      {error && (
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          {getAPIErrorMessage(error)}
        </Alert>
      )}
      <Stack direction="column" gap={2} mt={1}>
        {showExcEmailOtpUi ? (
          <>
            <CountDownButton
              buttonText={t('login.emailOTP.button.resendCode')}
              countDownText={t('login.emailOTP.newCodeIn')}
              showResendButton={false}
            />
            <Box textAlign="center">
              <Typography variant="body2" fontWeight={600} mb={0.5}>
                {t('login.emailOTP.excGuidance.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('login.emailOTP.excGuidance.body')}
              </Typography>
            </Box>
            <LoadingButton
              {...getDataTestId(`button-openBankingApp`)}
              onClick={onOpenBankingApp}
              variant="outlined"
              fullWidth
              type="button"
            >
              {t('login.emailOTP.button.openBankingApp')}
            </LoadingButton>
          </>
        ) : (
          <CountDownButton
            buttonText={t('login.emailOTP.button.resendCode')}
            countDownText={t('login.emailOTP.resendCodeIn')}
            onClick={onResend}
          />
        )}
        <LoadingButton
          {...getDataTestId(`verification-action-verify`)}
          disabled={otp.length < OTP_LENGTH || isPending}
          isLoading={isPending || isCreatingNewOperation}
          type="submit"
          fullWidth
        >
          {t('common.verify')}
        </LoadingButton>
      </Stack>
    </Form>
  );
}
